import { UploadedDocumentFile } from "../types/documents";

const HTTP_URL_PATTERN = /^https?:\/\//i;

export function getDriveFileIdFromUrl(value: string): string | null {
  const match = value.match(/drive\.google\.com\/file\/d\/([^/?#]+)/i);
  return match?.[1] || null;
}

function normalizeStringReference(value: string): UploadedDocumentFile | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      const nested = normalizeUploadedDocumentFiles(parsed);
      return nested.length > 0 ? nested[0] : null;
    } catch {
      // Treat malformed JSON as a legacy single value below.
    }
  }

  const driveFileId = getDriveFileIdFromUrl(trimmed);
  const isUrl = HTTP_URL_PATTERN.test(trimmed);

  return {
    id: driveFileId || trimmed,
    fileId: driveFileId || (!isUrl ? trimmed : undefined),
    url: isUrl ? trimmed : undefined,
    source: driveFileId ? "drive" : isUrl ? "external" : "hubspot",
  };
}

function normalizeObjectReference(value: Record<string, unknown>): UploadedDocumentFile | null {
  const rawUrl = String(value.url || value.webViewLink || value.link || "").trim();
  const rawFileId = String(value.fileId || value.file_id || "").trim();
  const rawId = String(value.id || "").trim();
  const driveFileId = rawUrl ? getDriveFileIdFromUrl(rawUrl) : null;
  const fileId = rawFileId || driveFileId || (!rawUrl ? rawId : "");
  const id = rawId || fileId || rawUrl;

  if (!id) return null;

  return {
    id,
    fileId: fileId || undefined,
    url: rawUrl || undefined,
    name: typeof value.name === "string" ? value.name : undefined,
    mimeType: typeof value.mimeType === "string" ? value.mimeType : typeof value.type === "string" ? value.type : undefined,
    uploadedAt: typeof value.uploadedAt === "string" ? value.uploadedAt : undefined,
    thumbnailUrl: typeof value.thumbnailUrl === "string"
      ? value.thumbnailUrl
      : typeof value.thumbnailLink === "string"
        ? value.thumbnailLink
        : undefined,
    previewData: typeof value.previewData === "string" ? value.previewData : undefined,
    source: driveFileId || rawUrl.includes("drive.google.com") ? "drive" : rawUrl ? "external" : "hubspot",
    // PR-Review-Finding: ohne description/originalId gingen Beschriftung und Original/Markiert-
    // Paarung verloren, sobald die Dateiliste nicht mehr aus dem lokalen Store (documentFiles[key])
    // kam, sondern aus der HubSpot-Property neu abgeleitet wurde (neuer Browser, gelöschter Local
    // Storage) — uploadedDocumentFilesToPropertyValue muss dafür beide Felder mit persistieren.
    description: typeof value.description === "string" ? value.description : undefined,
    originalId: typeof value.originalId === "string" ? value.originalId : undefined,
  };
}

export function normalizeUploadedDocumentFiles(input: unknown): UploadedDocumentFile[] {
  if (input === null || input === undefined) return [];

  if (Array.isArray(input)) {
    return input.flatMap((entry) => normalizeUploadedDocumentFiles(entry));
  }

  if (typeof input === "string") {
    const trimmed = input.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith("[")) {
      try {
        return normalizeUploadedDocumentFiles(JSON.parse(trimmed));
      } catch {
        // Treat malformed JSON as a legacy single value below.
      }
    }

    return trimmed
      .split(";")
      .map((entry) => normalizeStringReference(entry))
      .filter((entry): entry is UploadedDocumentFile => Boolean(entry));
  }

  if (typeof input === "object") {
    const normalized = normalizeObjectReference(input as Record<string, unknown>);
    return normalized ? [normalized] : [];
  }

  const normalized = normalizeStringReference(String(input));
  return normalized ? [normalized] : [];
}

export function mergeUploadedDocumentFiles(
  ...groups: Array<UploadedDocumentFile[] | undefined | null>
): UploadedDocumentFile[] {
  const seen = new Set<string>();
  const merged: UploadedDocumentFile[] = [];

  groups.flat().forEach((file) => {
    if (!file) return;
    const key = file.fileId || file.url || file.id;
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(file);
  });

  return merged;
}

// PR-Review-Finding: nur die rohen Links zu speichern verwirft beim Zurücklesen (normalizeObjectReference
// liest exakt diese Felder wieder ein) Name, Beschriftung und die originalId-Paar-Verknüpfung — nach einem
// neuen Browser/gelöschtem Local Storage erschienen Original und Markierung wieder als zwei getrennte,
// unbeschriftete Dateien. Deshalb wird pro Datei ein vollständiges Objekt statt nur des Links persistiert.
export function uploadedDocumentFilesToPropertyValue(files: UploadedDocumentFile[]): string {
  const values = files
    .filter((file) => Boolean((file.url || file.fileId || file.id) && (file.url || file.fileId || file.id)!.trim()))
    .map((file) => ({
      id: file.id,
      fileId: file.fileId,
      url: file.url,
      name: file.name,
      mimeType: file.mimeType,
      thumbnailUrl: file.thumbnailUrl,
      description: file.description ?? undefined,
      originalId: file.originalId ?? undefined,
    }));

  return JSON.stringify(values);
}

export function stripUploadedDocumentRuntimeData(file: UploadedDocumentFile): UploadedDocumentFile {
  const { previewData: _previewData, ...persistableFile } = file;
  return persistableFile;
}

export function stripUploadedDocumentFilesRuntimeData(files: UploadedDocumentFile[]): UploadedDocumentFile[] {
  return files.map(stripUploadedDocumentRuntimeData);
}
